import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CompleteJobFinancialsStep } from './CompleteJobFinancialsStep';
import { CompleteJobPaymentsStep } from './CompleteJobPaymentsStep';
import { CompleteJobSummaryStep } from './CompleteJobSummaryStep';
import type { JobWithRelations } from '@/types';

interface CompleteJobDialogProps {
  job: JobWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export interface JobFinancials {
  invoice_total: number;
  /** Internal parts cost (profit input). */
  total_parts_cost: number;
  /** Parts at sell price — what the customer is charged; must not exceed invoice_total. */
  total_parts_sell: number;
  toll_cost: number;
  work_summary: string;
  job_date: Date;
}

export interface JobPayment {
  amount: number;
  payment_method: string;
  payment_date: Date;
  notes?: string;
}

export function CompleteJobDialog({ job, open, onOpenChange, onComplete }: CompleteJobDialogProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [financials, setFinancials] = useState<JobFinancials>({
    invoice_total: job.invoice_total || 0,
    total_parts_cost: job.total_parts_cost || 0,
    total_parts_sell: job.total_parts_sell || 0,
    toll_cost: job.toll_cost || 0,
    work_summary: job.work_summary || '',
    job_date: new Date(),
  });
  const [payments, setPayments] = useState<JobPayment[]>([]);

  const handleClose = () => {
    setCurrentStep(1);
    setFinancials({
      invoice_total: job.invoice_total || 0,
      total_parts_cost: job.total_parts_cost || 0,
      total_parts_sell: job.total_parts_sell || 0,
      toll_cost: job.toll_cost || 0,
      work_summary: job.work_summary || '',
      job_date: new Date(),
    });
    setPayments([]);
    onOpenChange(false);
  };

  const handleFinancialsNext = (data: JobFinancials) => {
    setFinancials(data);
    setCurrentStep(2);
  };

  const handlePaymentsNext = (data: JobPayment[]) => {
    setPayments(data);
    setCurrentStep(3);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4">
            <span>Close & Post Job</span>
            <div className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                1
              </div>
              <div className="w-8 h-0.5 bg-muted" />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                2
              </div>
              <div className="w-8 h-0.5 bg-muted" />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                3
              </div>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">Complete the job by entering financials, recording payment, and confirming closure.</DialogDescription>
        </DialogHeader>

        {currentStep === 1 && (
          <CompleteJobFinancialsStep
            job={job}
            initialData={financials}
            onNext={handleFinancialsNext}
            onCancel={handleClose}
          />
        )}

        {currentStep === 2 && (
          <CompleteJobPaymentsStep
            financials={financials}
            initialPayments={payments}
            onNext={handlePaymentsNext}
            onBack={() => setCurrentStep(1)}
            onCancel={handleClose}
          />
        )}

        {currentStep === 3 && (
          <CompleteJobSummaryStep
            job={job}
            financials={financials}
            payments={payments}
            onComplete={handleComplete}
            onBack={() => setCurrentStep(2)}
            onCancel={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
